import { useEffect, useRef } from 'react'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/use-auth'
import { isJwtExpired } from '@/utils/jwt'
import { NOTIFICATIONS_QUERY_KEY } from '@/hooks/use-notifications'

const RECONNECT_DELAY_MS = 5_000

type NotificationStreamEnvelope = {
  domain?: string
}

/**
 * Live notifications stream. The server publishes lightweight signals
 * (notification id, category, unread count) — the DB is the source of truth,
 * so on any signal (and on reconnect) we just invalidate the notifications
 * cache and let TanStack Query refetch.
 *
 * SSE comment heartbeats (`: heartbeat`) still dispatch onmessage with empty
 * data — ignore those (same guard as useStreamingSse).
 */
export function useNotificationsSse(enabled = true) {
  const { token, isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const queryClientRef = useRef(queryClient)
  queryClientRef.current = queryClient

  useEffect(() => {
    if (!enabled || !isAuthenticated || !token || isJwtExpired(token)) {
      return
    }

    const controller = new AbortController()

    const invalidate = () => {
      void queryClientRef.current.invalidateQueries({
        queryKey: NOTIFICATIONS_QUERY_KEY,
      })
    }

    void fetchEventSource('/api/notifications/live', {
      headers: {
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
      openWhenHidden: true,
      onopen: async (response) => {
        if (response.ok) {
          // Catch up on anything missed while disconnected.
          invalidate()
          return
        }
        throw new Error(`Notifications stream failed to open (${response.status})`)
      },
      onmessage: (event) => {
        const raw = event.data?.trim()
        if (!raw) return

        try {
          const envelope = JSON.parse(raw) as NotificationStreamEnvelope
          if (envelope.domain !== 'notifications') return
        } catch {
          return
        }

        // Envelope contents are only a hint; refetch from the REST API.
        invalidate()
      },
      onerror: () => {
        // Returning a number tells fetchEventSource to retry after that delay.
        // Redis/API outages degrade gracefully — the bell catches up on reconnect.
        return RECONNECT_DELAY_MS
      },
    }).catch(() => {
      // Aborted or fatally closed; a token/auth change re-runs this effect.
    })

    return () => {
      controller.abort()
    }
  }, [enabled, isAuthenticated, token])
}
